import React, { useState } from "react";
import { Avatar, Button, Grid, Spin, message } from "antd";
import {
    CheckOutlined,
    ClockCircleOutlined,
    TeamOutlined,
    UserOutlined,
} from "@ant-design/icons";
import {
    useUpdateItemMutation,
    useItemsFine,
} from "../../../_actions/app/items";
import { useCurrentUser } from "../../../_actions/auth";
import AddUserToOrg from "./AddUserToOrg";
import log from "cslog";

const { useBreakpoint } = Grid;

function ManageBrandMembers({ id, members }) {
    const updateMutation = useUpdateItemMutation();
    const { data: currentUser } = useCurrentUser();
    const currentUserId = currentUser?.data?.id;
    const screens = useBreakpoint();
    const isMobile = !screens.sm;

    const memberList = members
        .filter((item) => item.role === "member")
        .map((item) => ({
            userId: item.directus_users_id.id,
            name: `${item.directus_users_id.first_name} ${item.directus_users_id.last_name}`.trim(),
            email: item.directus_users_id.email,
            picture: item.directus_users_id.avatar,
        }));

    const {
        data: pendingData,
        isLoading: pendingLoading,
        refetch: refetchPending,
    } = useItemsFine(
        "junction_directus_users_qdb_organization",
        `fields=*,directus_users_id.*&filter={"qdb_organization_id":{"_eq":"${id}"}, "role":{"_eq":"draft"}}`,
        true
    );

    log.d(pendingData, "Pending requests");

    const pendingRequests = pendingData
        ? pendingData
              .filter((item) => item.role === "draft")
              .map((item) => ({
                  junctionId: item.id,
                  userId: item.directus_users_id.id,
                  name: `${item.directus_users_id.first_name} ${item.directus_users_id.last_name}`.trim(),
                  email: item.directus_users_id.email,
                  picture: item.directus_users_id.avatar,
                  isCurrentUser: item.directus_users_id.id === currentUserId,
              }))
        : [];

    const [acceptingIds, setAcceptingIds] = useState(new Set());

    const acceptRequest = (junctionId) => {
        setAcceptingIds((prev) => new Set(prev).add(junctionId));
        updateMutation.mutate(
            {
                collection: "junction_directus_users_qdb_organization",
                id: junctionId,
                data: { role: "member" },
            },
            {
                onSuccess: () => {
                    refetchPending();
                    message.success("Member accepted");
                    setAcceptingIds((prev) => {
                        const next = new Set(prev);
                        next.delete(junctionId);
                        return next;
                    });
                },
                onError: () => {
                    setAcceptingIds((prev) => {
                        const next = new Set(prev);
                        next.delete(junctionId);
                        return next;
                    });
                },
            }
        );
    };

    const getInitials = (name) => {
        if (!name) return "?";
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    const avatarColors = [
        "#4f46e5",
        "#0891b2",
        "#059669",
        "#d97706",
        "#dc2626",
        "#7c3aed",
        "#db2777",
        "#0284c7",
    ];
    const getAvatarColor = (name) =>
        avatarColors[(name?.charCodeAt(0) ?? 0) % avatarColors.length];

    const MemberAvatar = ({ name, picture, faded }) =>
        picture ? (
            <Avatar
                src={picture}
                size={38}
                style={{ opacity: faded ? 0.7 : 1 }}
            />
        ) : (
            <Avatar
                size={38}
                style={{
                    backgroundColor: getAvatarColor(name),
                    fontWeight: 600,
                    fontSize: 14,
                    opacity: faded ? 0.7 : 1,
                }}
            >
                {getInitials(name)}
            </Avatar>
        );

    return (
        <div
            style={{
                maxWidth: isMobile ? "100%" : 600,
                fontFamily: "system-ui, -apple-system, sans-serif",
            }}
        >
            {/* Members */}
            <Section
                icon={<TeamOutlined />}
                title="Members"
                count={memberList.length || null}
            >
                {memberList.length > 0 && (
                    <div style={s.card}>
                        {memberList.map((item, i) => (
                            <div
                                key={item.userId}
                                style={{
                                    ...s.row,
                                    borderBottom:
                                        i < memberList.length - 1
                                            ? "1px solid #f3f4f6"
                                            : "none",
                                }}
                            >
                                <MemberAvatar
                                    name={item.name}
                                    picture={item.picture}
                                />
                                <div style={s.info}>
                                    <div style={s.name}>
                                        {item.name}
                                        {item.userId === currentUserId && (
                                            <YouTag />
                                        )}
                                    </div>
                                    <div style={s.email}>{item.email}</div>
                                </div>
                                <div style={s.activeBadge}>
                                    <CheckOutlined
                                        style={{
                                            fontSize: 11,
                                            color: "#10b981",
                                        }}
                                    />
                                    <span
                                        style={{
                                            fontSize: 12,
                                            color: "#10b981",
                                            marginLeft: 4,
                                        }}
                                    >
                                        Active
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            {/* Pending Requests */}
            <Section
                icon={<ClockCircleOutlined />}
                title="Invited Members"
                count={
                    !pendingLoading && pendingRequests.length > 0
                        ? pendingRequests.length
                        : null
                }
                countStyle={{ backgroundColor: "#fef3c7", color: "#d97706" }}
            >
                {pendingLoading ? (
                    <div style={s.loadingRow}>
                        <Spin size="small" />
                        <span
                            style={{
                                color: "#9ca3af",
                                fontSize: 13,
                                marginLeft: 8,
                            }}
                        >
                            Loading…
                        </span>
                    </div>
                ) : pendingRequests.length === 0 ? (
                    <p style={s.emptyText}>No pending requests</p>
                ) : (
                    <div style={s.card}>
                        {pendingRequests.map((item, i) => (
                            <div
                                key={item.junctionId}
                                style={{
                                    ...s.row,
                                    borderBottom:
                                        i < pendingRequests.length - 1
                                            ? "1px solid #f3f4f6"
                                            : "none",
                                    backgroundColor: item.isCurrentUser
                                        ? "#fffbeb"
                                        : "#fff",
                                }}
                            >
                                <MemberAvatar
                                    name={item.name}
                                    picture={item.picture}
                                    faded
                                />
                                <div style={s.info}>
                                    <div style={s.name}>
                                        {item.name}
                                        {item.isCurrentUser && <YouTag />}
                                    </div>
                                    <div style={s.email}>{item.email}</div>
                                    {item.isCurrentUser && (
                                        <div style={s.pendingNote}>
                                            Your request is awaiting admin
                                            approval
                                        </div>
                                    )}
                                </div>
                                {item.isCurrentUser ? (
                                    <div style={s.pendingChip}>
                                        <ClockCircleOutlined
                                            style={{ fontSize: 11 }}
                                        />
                                        <span style={{ marginLeft: 4 }}>
                                            Pending
                                        </span>
                                    </div>
                                ) : (
                                    <Button
                                        size={isMobile ? "default" : "small"}
                                        loading={acceptingIds.has(
                                            item.junctionId
                                        )}
                                        onClick={() =>
                                            acceptRequest(item.junctionId)
                                        }
                                        style={s.acceptBtn}
                                    >
                                        Accept
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            {/* Add Member */}
            {/* <Section icon={<UserOutlined />} title="Invite Member"> */}
            <AddUserToOrg
                existingEmails={[
                    ...memberList.map((m) => m.email),
                    ...pendingRequests.map((p) => p.email),
                ]}
            />
            {/* </Section> */}
        </div>
    );
}

function Section({ icon, title, count, countStyle, children }) {
    return (
        <div style={{ marginBottom: 28 }}>
            <div style={s.sectionHead}>
                <span style={{ color: "#9ca3af", fontSize: 14 }}>{icon}</span>
                <span style={s.sectionTitle}>{title}</span>
                {count != null && (
                    <span style={{ ...s.badge, ...countStyle }}>{count}</span>
                )}
            </div>
            {children}
        </div>
    );
}

function YouTag() {
    return (
        <span
            style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#6366f1",
                backgroundColor: "#eef2ff",
                borderRadius: 4,
                padding: "1px 6px",
                letterSpacing: "0.02em",
            }}
        >
            you
        </span>
    );
}

const s = {
    card: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        overflow: "hidden",
        backgroundColor: "#fff",
    },
    row: {
        display: "flex",
        alignItems: "center",
        padding: "11px 16px",
        gap: 12,
    },
    info: { flex: 1, minWidth: 0 },
    name: {
        fontSize: 14,
        fontWeight: 500,
        color: "#111827",
        display: "flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    email: {
        fontSize: 12,
        color: "#9ca3af",
        marginTop: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    pendingNote: {
        fontSize: 11,
        color: "#d97706",
        marginTop: 3,
    },
    activeBadge: {
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        backgroundColor: "#f0fdf4",
        borderRadius: 6,
        padding: "3px 8px",
    },
    acceptBtn: {
        flexShrink: 0,
        borderRadius: 6,
        fontWeight: 500,
        borderColor: "#a7f3d0",
        color: "#059669",
        backgroundColor: "#f0fdf4",
    },
    pendingChip: {
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        backgroundColor: "#fef3c7",
        color: "#d97706",
        borderRadius: 6,
        padding: "3px 8px",
        fontSize: 11,
        fontWeight: 500,
    },
    sectionHead: {
        display: "flex",
        alignItems: "center",
        gap: 7,
        marginBottom: 10,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 700,
        color: "#374151",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
    },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: "#f3f4f6",
        color: "#6b7280",
        fontSize: 11,
        fontWeight: 700,
        padding: "0 6px",
    },
    loadingRow: {
        display: "flex",
        alignItems: "center",
        padding: "12px 0",
    },
    emptyText: {
        fontSize: 13,
        color: "#9ca3af",
        margin: 0,
        padding: "2px 0",
    },
};

export default ManageBrandMembers;
